import { useEffect } from 'react'
import * as logger from 'loglevel'
// Import centralized web component registration (ensures all components are registered early)
import '../WebComponents'
import { upgradeCustomElements } from '../WebComponents'

/**
 * Binds avatars, QR codes, nav buttons, action-clicks, and form-submits
 * every time display content is updated (full replace or anchor/targets inject).
 * Depends on lastMessageId and displayRevision so anchor-injected content
 * (e.g. anear-timeout wrapping anear-action/anear-form) gets initialized.
 */
export default function useAppDisplayDomSetup(
  frameContentRef,
  {
    userAuthContainer,
    anearEvent,
    lastMessageId,
    displayRevision = 0,
    onNavClick,
    onActionClick,
    onFormSubmit,
    onQrClick,
    onHelpOpen,
    onFileUploadRequest,
    onWorkflowStart,
    onWorkflowPoll,
    onEventClock,
    onPublishAction,
  }
) {
  useEffect(() => {
    const root = frameContentRef.current
    if (!root || lastMessageId == null) return

    // Upgrade any custom elements that were parsed before registration
    // This is especially important for Safari which can parse HTML before
    // custom elements are defined
    upgradeCustomElements(root)

    // — handle buttons outside forms that use form="formId" attribute (HTML5 feature) —
    // Set up event delegation at the root level to catch clicks on buttons with form attributes
    // that reference anear-form elements
    // Define this outside requestAnimationFrame so we can clean it up
    const handleExternalFormButton = (e) => {
      // Double-check root is still valid
      if (!frameContentRef.current) return
      
      // Find the submit button
      let submitButton = null
      if (e.target.type === 'submit' && (e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT')) {
        submitButton = e.target
      } else {
        submitButton = e.target.closest('button[type="submit"], input[type="submit"]')
      }

      if (!submitButton) return

      const formId = submitButton.getAttribute('form')
      if (!formId) return // Not a button with form attribute

      // Find the anear-form by ID
      const form = root.querySelector(`anear-form#${formId}`)
      if (!form) return // Form not found or not an anear-form

      // Check if the form is initialized (has the submit handler)
      if (!form._onFormSubmit) return // Form not ready yet

      // Prevent default and stop propagation
      e.preventDefault()
      e.stopPropagation()

      // Create a synthetic submit event
      const submitEvent = new Event('submit', {
        bubbles: true,
        cancelable: true
      })
      // Set the submitter property
      Object.defineProperty(submitEvent, 'submitter', {
        value: submitButton,
        writable: false,
        configurable: false
      })
      // Set target to the form element itself so the handler can find it
      // (currentTarget will also be the form when dispatched on form)
      Object.defineProperty(submitEvent, 'target', {
        value: form,
        writable: false,
        configurable: false
      })
      // Dispatch the event on the form element
      form.dispatchEvent(submitEvent)
    }

    // Use capture phase to catch clicks before they bubble
    root.addEventListener('click', handleExternalFormButton, true)

    // Wait for custom elements to be upgraded (they upgrade in a microtask)
    // This is critical for Shadow DOM where elements are created via dangerouslySetInnerHTML
    // Use a double RAF to ensure DOM is fully settled and custom elements upgraded
    // (First RAF: browser paint, Second RAF: next tick after microtasks)
    let rafId2 = null
    const rafId = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        // Double-check root is still valid (component might have unmounted)
        if (!frameContentRef.current) return

        // Upgrade custom elements again to catch any that weren't upgraded yet
        // This is important for Shadow DOM where elements might be created after initial upgrade
        upgradeCustomElements(root)

      // — avatars (web components only) —
      const avatars = root.querySelectorAll('anear-user-avatar')
      avatars.forEach(component => {
        const avatarUrl = userAuthContainer?.avatarUrl?.()
        const userName = userAuthContainer?.name?.()
        if (avatarUrl && userName && typeof component.initialize === 'function') {
          component.initialize(avatarUrl, userName)
        }
      })

      // — QR codes (web components only) —
      const qrCodes = root.querySelectorAll('anear-qr-code')
      qrCodes.forEach(component => {
        const qrImageUrl = anearEvent?.attributes?.['qr-image-url']
        const eventName = anearEvent?.name
        if (qrImageUrl && eventName && typeof component.initialize === 'function') {
          component.initialize(qrImageUrl, eventName, onQrClick)
        }
      })

      // — nav buttons (web components only) —
      const navs = root.querySelectorAll('anear-nav')
      navs.forEach(component => {
        if (onNavClick && typeof component.initialize === 'function') {
          component.initialize(onNavClick)
        }
      })

      // — action clicks (web components only) —
      const actions = root.querySelectorAll('anear-action')
      actions.forEach(component => {
        if (onActionClick && typeof component.initialize === 'function') {
          component.initialize(onActionClick)
        } else {
          logger.warn(`[useAppDisplayDomSetup] Skipping anear-action initialization: onActionClick=${!!onActionClick}, hasInitialize=${typeof component.initialize === 'function'}`)
        }
      })

      // - help triggers (web components only) -
      const helpEls = root.querySelectorAll('anear-help')
      helpEls.forEach(component => {
        if (onHelpOpen && typeof component.initialize === 'function') {
          component.initialize(onHelpOpen)
        }
      })

      // — form submits (web components only) —
      const forms = root.querySelectorAll('anear-form')
      forms.forEach(component => {
        if (onFormSubmit && typeof component.initialize === 'function') {
          component.initialize(onFormSubmit)
        }
      })

      // — file and image upload components —
      const fileUploaders = root.querySelectorAll('anear-file-upload, anear-image-upload')
      fileUploaders.forEach(component => {
        const eventId = anearEvent?.id
        if (onFileUploadRequest && eventId && typeof component.initialize === 'function') {
          component.initialize(onFileUploadRequest, eventId)
        }
      })

      const libraryUploads = root.querySelectorAll('anear-media-upload')
      libraryUploads.forEach(component => {
        const eventId = anearEvent?.id
        if (typeof component.initialize !== 'function') return
        component.initialize({
          onUploadRequest: onFileUploadRequest,
          eventId,
          onWorkflowStart,
          publishAction: onPublishAction
        })
      })

      const cameras = root.querySelectorAll('anear-camera')
      cameras.forEach(component => {
        const eventId = anearEvent?.id
        if (typeof component.initialize !== 'function') return
        component.initialize({
          onUploadRequest: onFileUploadRequest,
          eventId,
          onWorkflowStart,
          onEventClock,
          publishAction: onPublishAction
        })
      })

      // — generic workflow trigger/poll components —
      const workflows = root.querySelectorAll('anear-workflow')
      workflows.forEach(component => {
        const eventId = anearEvent?.id
        if (onWorkflowStart && typeof component.initialize === 'function') {
          component.initialize({ start: onWorkflowStart, poll: onWorkflowPoll, eventId })
        }
      })
      })
    })

    // — autofocus helpers (web components only) —
    // Web component approach: <anear-autofocus behavior="end"><textarea></textarea></anear-autofocus>
    // The component handles its own autofocus logic in connectedCallback

    // Cleanup function to remove the event listener and cancel animation frames
    return () => {
      cancelAnimationFrame(rafId)
      if (rafId2) cancelAnimationFrame(rafId2)
      if (root) {
      root.removeEventListener('click', handleExternalFormButton, true)
      }
    }
  }, [
    frameContentRef,
    lastMessageId,
    displayRevision,
    userAuthContainer,
    anearEvent,
    onNavClick,
    onActionClick,
    onFormSubmit,
    onQrClick,
    onHelpOpen,
    onFileUploadRequest,
    onWorkflowStart,
    onWorkflowPoll,
    onEventClock,
    onPublishAction,
  ])
}
